import React, { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import type { DocumentFieldsResponse, MissingReferenceInfo, PendingDocument } from '@schemas/api';
import { ReferenceSelectionModal } from './ReferenceSelectionModal';
import type { MissingReferenceResolution } from './ReferenceSelectionModal';
import { VendorCreationForm } from './VendorCreationForm';
import { PurchaseOrderCreationForm } from './PurchaseOrderCreationForm';

interface MissingReferencesPanelProps {
  missingReferences?: MissingReferenceInfo[];
  document: PendingDocument;
  documentData?: DocumentFieldsResponse;
  extractionData?: Record<string, any>;
  onReferencesResolved?: (resolution?: MissingReferenceResolution) => void;
}

export const MissingReferencesPanel: React.FC<MissingReferencesPanelProps> = ({
  missingReferences = [],
  document,
  documentData,
  extractionData,
  onReferencesResolved,
}) => {
  const [selectedReference, setSelectedReference] = useState<MissingReferenceInfo | null>(null);
  const [modalMode, setModalMode] = useState<'search' | 'create'>('search');

  if (missingReferences.length === 0) {
    return null;
  }

  const unresolved = missingReferences.filter((ref) => ref.status === 'unresolved');

  const handleOpenSearchModal = (ref: MissingReferenceInfo) => {
    setSelectedReference(ref);
    setModalMode('search');
  };

  const handleOpenCreateForm = (ref: MissingReferenceInfo) => {
    setSelectedReference(ref);
    setModalMode('create');
  };

  const handleModalClose = () => {
    setSelectedReference(null);
  };

  const handleSuccess = (resolution?: MissingReferenceResolution) => {
    handleModalClose();
    if (onReferencesResolved) {
      onReferencesResolved(resolution);
    }
  };

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
      <h3 className="font-semibold text-blue-900 mb-3">Missing References</h3>

      {unresolved.length > 0 ? (
        <div className="space-y-2">
          {unresolved.map((ref) => (
            <div
              key={`${ref.field_name}-${ref.ref_value}`}
              className="flex items-center justify-between bg-white rounded border border-blue-100 p-3"
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {ref.field_name}: <span className="text-blue-600">{ref.ref_value}</span>
                </p>
                <p className="text-xs text-gray-500">
                  {ref.ref_object_type}
                  {ref.is_critical && <span className="ml-2 text-red-600 font-semibold">*Required</span>}
                </p>
              </div>
              <div className="ml-4 flex gap-2">
                <button
                  onClick={() => handleOpenSearchModal(ref)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition"
                >
                  <Search className="h-4 w-4" />
                  Find
                </button>
                <button
                  onClick={() => handleOpenCreateForm(ref)}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition"
                >
                  <Plus className="h-4 w-4" />
                  Create
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-blue-800">All missing references have been resolved!</p>
      )}

      {/* Search Modal */}
      {selectedReference && modalMode === 'search' && (
        <ReferenceSelectionModal
          missingReference={selectedReference}
          onSuccess={handleSuccess}
          onClose={handleModalClose}
        />
      )}

      {/* Create Forms */}
      {selectedReference && modalMode === 'create' && selectedReference.ref_object_type === 'vendor' && (
        <VendorCreationForm
          initialVendorName={selectedReference.ref_value}
          documentData={documentData}
          extractionData={extractionData}
          missingReferenceId={selectedReference.id}
          onSuccess={handleSuccess}
          onClose={handleModalClose}
        />
      )}

      {selectedReference && modalMode === 'create' && selectedReference.ref_object_type === "purchase_order" && (
        <PurchaseOrderCreationForm
          initialPoNumber={selectedReference.ref_value}
          vendorId={undefined}
          missingReferenceId={selectedReference.id}
          onSuccess={handleSuccess}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
};
